import { memo, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import Pane from 'components/Common/Pane'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import { getThemeClassName } from 'lib/theme/themeUtils'
import useRebrandVersion from 'hooks/useRebrandVersion'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import { getContentfulHeroOfferCarouselPalette } from './contentfulRebrandSurfaces'

const Wrapper = styled(Pane)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};

 ${mediaQueryUp.desktop} {
   padding-top: ${rem(80)};
 }

  /*
   * Product boundary: Contentful hero carousels (e.g. golf) use beige under rebrand
   * for every CMS hero carousel instance — not path-scoped.
   * Path-scoped beige for dark video blocks lives in ContentfulVideoBlock only;
   * do not globalise that video treatment to match this hero rule.
   *
   * Legacy keeps paletteType=inverse with full tokens (no partial CSS remap).
   * Beige = Neutral / Surface / LightBackground under rebrand only.
   * Prefer && under body… (not nested &&) — nested && emits a broken selector.
   */
  body.${REBRAND_PALETTE_CLASS} && {
    background-color: var(--palette-neutral-surface-light-background);
  }
`

interface Props {
  title: string
  filters: App.OfferListFilters
  overrideOfferIds?: Array<string>
}

function HeroOfferCarouselSection(props: Props) {
  const filters = useMemo(() => {
    return {
      ...props.filters,
      useUnifiedSearchEndpoint: true,
    }
  }, [props.filters])

  const overrideOfferList = useMemo<App.OfferList | undefined>(() => {
    if (!props.overrideOfferIds?.length) return undefined
    return { offerIds: props.overrideOfferIds, fetching: false, error: null, key: 'contentful-override' }
  }, [props.overrideOfferIds])

  const isRebrand = useRebrandVersion() === 'rebrand'

  return (
    <Wrapper paletteType={getContentfulHeroOfferCarouselPalette(isRebrand)}>
      <Group direction="vertical" gap={24}>
        <LayoutContainer>
          <Heading variant="heading2" dangerouslySetInnerHTML={{ __html: props.title }} />
        </LayoutContainer>
        <OfferListCarousel
          tileStyle="hero"
          filters={overrideOfferList ? undefined : filters}
          overrideOfferList={overrideOfferList}
          className={getThemeClassName({ desktop: 'default' })}
        />
      </Group>
    </Wrapper>
  )
}

export default memo(HeroOfferCarouselSection)
